import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  ThemeProvider as StyledThemeProvider,
  createGlobalStyle,
} from 'styled-components';

// Create a Theme Context
const ThemeContext = createContext();

// Define Light and Dark Mode Themes
const lightTheme = {
  text: 'hsl(0, 24%, 30%);',
  glassBackground: 'rgba(255, 255, 255, 0.2)',
  background: 'hsl(0, 24%, 96%)',
  tileBackground: 'hsl(0, 0%, 100%)',
  borderColor: 'hsl(0, 24%, 90%)',
  border: '1px solid hsla(0, 24%, 90%, 1);',
  iconColor: 'hsl(0, 24%, 30%)',
  toggleIcon: '🌞',
};

const darkTheme = {
  background: 'black',
  iconColor: 'black',
  tileBackground: 'hsla(0, 0%,50%, 0.2);',
  text: 'hsl(0, 24%, 96%)',
  borderColor: 'hsla(0, 0%, 100%, 0.1);',
  border: '1px solid hsla(0, 0%, 100%, 0.1);',
  toggleIcon: '🌙',
};

// Global Styles for Styled Components

const GlobalStyles = createGlobalStyle`
/* http://meyerweb.com/eric/tools/css/reset/
   v2.0 | 20110126
   License: none (public domain)
*/
html, body, div, span, applet, object, iframe,
h1, h2, h3, h4, h5, h6, p, blockquote, pre,
a, abbr, acronym, address, big, cite, code,
del, dfn, em, img, ins, kbd, q, s, samp,
small, strike, strong, sub, sup, tt, var,
b, u, i, center,
dl, dt, dd, ol, ul, li,
fieldset, form, label, legend,
table, caption, tbody, tfoot, thead, tr, th, td,
article, aside, canvas, details, embed,
figure, figcaption, footer, header, hgroup,
menu, nav, output, ruby, section, summary,
time, mark, audio, video {
	margin: 0;
	padding: 0;
	border: 0;
  font-size: 100%;
	vertical-align: baseline;
}
/* HTML5 display-role reset for older browsers */
article, aside, details, figcaption, figure,
footer, header, hgroup, menu, nav, section {
	display: block;
}
ol, ul {
	list-style: none;
}
blockquote, q {
	quotes: none;
}
blockquote:before, blockquote:after,
q:before, q:after {
	content: '';
	content: none;
}
table {
	border-collapse: collapse;
	border-spacing: 0;
}


/* GLOBAL STYLES */
*,
*:before,
*:after {
  box-sizing: border-box;
  line-height: 1.45;
  font-family: 'Raleway', sans-serif;

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}

#root {
  /*
    Create a stacking context, without a z-index.
    This ensures that all portal content (modals and tooltips) will
    float above the app.
  */
  isolation: isolate;
}

html, body, #root {
  height: 100%;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background};
}
`;

export default GlobalStyles;

export const ThemeProvider = ({ children }) => {
  const storedTheme = localStorage.getItem('darkMode');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const defaultMode =
    storedTheme !== null ? JSON.parse(storedTheme) : prefersDark;

  const [isDarkMode, setIsDarkMode] = useState(defaultMode);

  // ✅ Save dark mode preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => !prevMode); // Toggle mode
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <StyledThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <GlobalStyles />
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom Hook to Use Theme
export const useTheme = () => useContext(ThemeContext);
